import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Drawer,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  IconButton,
  Box,
  Divider,
  Collapse,
} from "@mui/material";
import { Menu as MenuIcon } from "@mui/icons-material";
import {
  Dashboard as DashboardIcon,
  People as PeopleIcon,
  Article as ArticleIcon,
  Announcement as AnnouncementIcon,
  Reviews as ReviewsIcon,
  Instagram as InstagramIcon,
  ExpandLess,
  ExpandMore,
  Event,
  Collections,
} from "@mui/icons-material";
import BiotechIcon from "@mui/icons-material/Biotech";
import PaymentsIcon from "@mui/icons-material/Payments";
import logo from "../../assets/Logofile/logo (2).png";
import favicon from "../../assets/Logofile/Favicon.png";
import routes from "../../services/routes/SidebarRoutes";
import VideoLibraryIcon from "@mui/icons-material/VideoLibrary";
import TvIcon from "@mui/icons-material/Tv";
import LanguageIcon from "@mui/icons-material/Language";
import AssignmentIndIcon from "@mui/icons-material/AssignmentInd";

const drawerWidth = 240;
const collapsedWidth = 72;

const Sidebar = ({ collapsed, sidebarOpen, setSidebarOpen }) => {
  const navigate = useNavigate();
  const location = useLocation();

  const [isMobile, setIsMobile] = useState(window.innerWidth <= 900);
  const [openMenus, setOpenMenus] = useState({});

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 900);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const handleNavigation = (path) => {
    navigate(path);
    if (isMobile) setSidebarOpen(false);
  };

  const handleMenuClick = (path) => {
    setOpenMenus((prev) => ({
      ...prev,
      [path]: !prev[path],
    }));
  };

  // Custom icons mapping
  const iconComponents = {
    dashboard: <DashboardIcon fontSize="small" />,
    team: <PeopleIcon fontSize="small" />,
    blogs: <ArticleIcon fontSize="small" />,
    press: <AnnouncementIcon fontSize="small" />,
    donor: <ReviewsIcon fontSize="small" />,
    instagram: <InstagramIcon fontSize="small" />,
    gallery: <Collections fontSize="small" />,
    event: <Event fontSize="small" />,
    video: <VideoLibraryIcon fontSize="small" />,
    tv: <TvIcon fontSize="small" />,
    media: <LanguageIcon fontSize="small" />,
    assignment: <AssignmentIndIcon fontSize="small" />,
    organCategory: <BiotechIcon fontSize="small" />,
    payment: <PaymentsIcon fontSize="small" />,
  };

  return (
    <Drawer
      variant={isMobile ? "temporary" : "permanent"}
      open={sidebarOpen}
      onClose={() => setSidebarOpen(false)}
      ModalProps={{ keepMounted: true }}
      sx={{
        width: collapsed ? collapsedWidth : drawerWidth,
        flexShrink: 0,
        "& .MuiDrawer-paper": {
          width: collapsed ? collapsedWidth : drawerWidth,
          boxSizing: "border-box",
          background: "#ffffff",
          color: "#333",
          borderRight: "1px solid #e0e0e0",
          transition: "width 0.3s",
          fontFamily: '"Poppins", sans-serif', // Add Poppins as default font
        },
        zIndex: 1200,
      }}
    >
      <Toolbar
        className="poppins-font"
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: collapsed ? "center" : "space-between",
          px: 2,
          py: 3,
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            width: "100%",
            justifyContent: collapsed ? "center" : "flex-start",
          }}
        >
          <img
            src={collapsed ? favicon : logo}
            alt="Logo"
            style={{ height: collapsed ? 28 : 40 }}
          />
        </Box>
        {isMobile && (
          <IconButton
            className="poppins-font"
            onClick={() => setSidebarOpen(false)}
            sx={{ color: "#333", ml: "auto" }}
            aria-label="Close sidebar"
          >
            <MenuIcon />
          </IconButton>
        )}
      </Toolbar>
      <Divider sx={{ borderColor: "#e0e0e0" }} />
      <List className="poppins-font" sx={{ flexGrow: 1, px: 1, py: 0 }}>
        {routes.map((route) => (
          <React.Fragment key={route.path || route.label}>
            {route.children ? (
              // Parent menu item with children
              <>
                <ListItemButton
                  className="poppins-font"
                  onClick={() => handleMenuClick(route.label)}
                  sx={{
                    borderRadius: 2,
                    mb: 1,
                    backgroundColor: "transparent",
                    color: "#555",
                    "&:hover": {
                      backgroundColor: "#f5f5f5",
                    },
                    justifyContent: collapsed ? "center" : "flex-start",
                    px: collapsed ? 0 : 2,
                    py: 1.5,
                  }}
                >
                  <ListItemIcon
                    sx={{
                      color: "#555",
                      minWidth: 0,
                      mr: collapsed ? 0 : 2,
                      justifyContent: "center",
                    }}
                  >
                    {iconComponents[route.icon] || <DashboardIcon />}
                  </ListItemIcon>
                  {!collapsed && (
                    <>
                      <ListItemText
                        className="poppins-font"
                        primary={route.label}
                        primaryTypographyProps={{ className: "poppins-font" }}
                      />
                      {openMenus[route.label] ? <ExpandLess /> : <ExpandMore />}
                    </>
                  )}
                </ListItemButton>

                {/* Nested menu items */}
                <Collapse
                  in={openMenus[route.label]}
                  timeout="auto"
                  unmountOnExit
                >
                  <List component="div" disablePadding>
                    {route.children.map((child) => (
                      <ListItemButton
                        key={child.path}
                        className="poppins-font"
                        selected={location.pathname === child.path}
                        onClick={() => handleNavigation(child.path)}
                        sx={{
                          pl: collapsed ? 2 : 4,
                          borderRadius: 2,
                          mb: 1,
                          backgroundColor:
                            location.pathname === child.path
                              ? "#f0f4ff"
                              : "transparent",
                          color:
                            location.pathname === child.path
                              ? "#3A6FF8"
                              : "#555",
                          "&:hover": {
                            backgroundColor: "#f5f5f5",
                          },
                        }}
                      >
                        <ListItemIcon
                          sx={{
                            color:
                              location.pathname === child.path
                                ? "#3A6FF8"
                                : "#555",
                            minWidth: 0,
                            mr: collapsed ? 0 : 2,
                            justifyContent: "center",
                          }}
                        >
                          {iconComponents[child.icon] || <DashboardIcon />}
                        </ListItemIcon>
                        {!collapsed && (
                          <ListItemText
                            primary={child.label}
                            className="poppins-font"
                            primaryTypographyProps={{
                              className: "poppins-font",
                            }}
                          />
                        )}
                      </ListItemButton>
                    ))}
                  </List>
                </Collapse>
              </>
            ) : (
              // Regular menu item
              <ListItemButton
                className="poppins-font"
                selected={location.pathname === route.path}
                onClick={() => handleNavigation(route.path)}
                sx={{
                  borderRadius: 2,
                  mb: 1,
                  backgroundColor:
                    location.pathname === route.path
                      ? "#f0f4ff"
                      : "transparent",
                  color: location.pathname === route.path ? "#3A6FF8" : "#555",
                  "&:hover": { backgroundColor: "#f5f5f5" },
                  justifyContent: collapsed ? "center" : "flex-start",
                  px: collapsed ? 0 : 1, // less horizontal padding
                  py: 1, // less vertical padding
                  minHeight: 36, // reduce height
                }}
              >
                <ListItemIcon
                  sx={{
                    color:
                      location.pathname === route.path ? "#3A6FF8" : "#555",
                    minWidth: 0,
                    mr: collapsed ? 0 : 1,
                    justifyContent: "center",
                    fontSize: 18, // smaller icon
                  }}
                >
                  {iconComponents[route.icon] || (
                    <DashboardIcon fontSize="small" />
                  )}
                </ListItemIcon>
                {!collapsed && (
                  <ListItemText
                    primary={route.label}
                    className="poppins-font"
                    primaryTypographyProps={{
                      className: "poppins-font",
                      sx: { fontSize: 14, fontWeight: 500 }, // smaller font
                    }}
                    sx={{ m: 0 }}
                  />
                )}
              </ListItemButton>
            )}
          </React.Fragment>
        ))}
      </List>
    </Drawer>
  );
};

export default Sidebar;
